<template>
  <li
    :id="props.optionId"
    :class="optionItemClasses"
    role="option"
    :aria-selected="isSelected"
    :aria-disabled="props.disabled || undefined"
    :data-cy="`ds-option-list__item--${convertStringToDataCy(String(props.option.value ?? 'null'))}`"
    tabindex="-1"
    @click="selectOption(props.option)"
    @mousedown.prevent
  >
    <div
      :class="DS_OPTION_LIST_CLASSES.optionContent"
      data-cy="ds-option-list__option-content"
    >
      <DSCheckbox
        v-if="props.multiple"
        :model-value="isSelected"
        data-cy="ds-option-list__item-checkbox"
        :disabled="props.disabled"
        :option="{ value: props.option.value }"
      />
      <img
        v-if="props.option.avatarUrl"
        :src="props.option.avatarUrl"
        :alt="props.option.label"
        :class="DS_OPTION_LIST_CLASSES.avatar"
        data-cy="ds-option-list__avatar"
      />

      <DSFlag
        v-else-if="props.option.flag"
        :flag="props.option.flag"
        :size="DS_FLAG_SIZES.Small"
        data-cy="ds-option-list__flag"
      />

      <DSIcon
        v-else-if="props.option.icon"
        :icon="props.option.icon as DSIconName"
        :class="DS_OPTION_LIST_CLASSES.icon"
        data-cy="ds-option-list__icon"
      />

      <div :class="DS_OPTION_LIST_CLASSES.details">
        <span
          :class="DS_OPTION_LIST_CLASSES.text"
          data-cy="ds-option-list__text"
        >
          {{ props.option.label }}
        </span>
        <span
          v-if="props.option.description"
          :class="DS_OPTION_LIST_CLASSES.description"
          data-cy="ds-option-list__description"
        >
          {{ props.option.description }}
        </span>
      </div>
    </div>

    <template v-if="hasSuffixSlot">
      <div
        :class="DS_OPTION_LIST_CLASSES.suffix"
        data-cy="ds-option-list__suffix"
      >
        <slot
          name="suffix"
          :option="props.option"
        />
      </div>
    </template>
  </li>
</template>

<script setup lang="ts">
import { computed, toRef, useSlots } from "vue";
import DSIcon from "../../../primitives/icon/components/DSIcon.vue";
import DSFlag from "../../../primitives/flag/components/DSFlag.vue";
import { DSOptionListItemProps, DSOptionListModelValue } from "../types";
import { convertStringToDataCy } from "@/src/shared/utils/playwright";
import { DS_OPTION_LIST_CLASSES } from "@/src/design_system/form_elements/option_list/constants/classes";
import DSCheckbox from "@/src/design_system/form_elements/checkbox/components/DSCheckbox.vue";
import { DS_FLAG_SIZES } from "@/src/design_system/primitives/flag/types";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { useOptionListSelection } from "@/src/design_system/form_elements/option_list/composables/useOptionListSelection";
import { normalizeModelValue } from "@/src/design_system/form_elements/option_list/utils/optionListUtils";

const props = withDefaults(defineProps<DSOptionListItemProps>(), {
  optionId: undefined,
  active: false,
});

const modelValue = defineModel<DSOptionListModelValue>();

const slots = useSlots();

const hasSuffixSlot = computed(() => Boolean(slots.suffix));

const optionItemClasses = computed(() => {
  return {
    [DS_OPTION_LIST_CLASSES.item]: true,
    [DS_OPTION_LIST_CLASSES.hoverable]: !props.disabled,
    [DS_OPTION_LIST_CLASSES.selected]: isSelected.value,
    [DS_OPTION_LIST_CLASSES.focused]: props.active,
    [DS_OPTION_LIST_CLASSES.disabled]: props.disabled,
  };
});

const isSelected = computed(() => normalizeModelValue(modelValue.value).includes(props.option.value));

const { selectOption } = useOptionListSelection({
  modelValue,
  multiple: toRef(props, "multiple"),
  disabled: toRef(props, "disabled"),
});
</script>
